import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Clock, Mail, Heart, CalendarDays, Globe } from 'lucide-react';

const plans = [
  {
    name: 'Starter',
    tagline: 'For small businesses and sole traders',
    description: 'Everything you need to never miss a call during business hours.',
    highlight: false,
    features: [
      'Business hours coverage (Mon–Fri, 9am–6pm)',
      'Call answering + message taking',
      'Email delivery of messages',
      'Standard call script',
      'Free onboarding call',
    ],
  },
  {
    name: 'Growth',
    tagline: 'For growing SMEs',
    description: 'Extended coverage with smarter call handling as your team scales.',
    highlight: true,
    features: [
      'Extended hours (8am–8pm, Mon–Sat)',
      'Call answering, message taking + transfers',
      'SMS + email message delivery',
      'Custom call script + FAQ handling',
      'Appointment booking',
      'Monthly reporting',
      'Free onboarding call',
    ],
  },
  {
    name: 'Enterprise',
    tagline: 'For high-volume or 24/7 needs',
    description: 'A fully dedicated extension of your team, around the clock.',
    highlight: false,
    features: [
      '24/7/365 coverage including bank holidays',
      'Full virtual receptionist service',
      'Call handling, transfers + email monitoring',
      'Duty of Care outbound calls',
      'Dedicated team briefed on your business',
      'Client portal + call recordings',
      'Priority support',
    ],
  },
];

const addons = [
  { name: '24/7 Upgrade',                  icon: Clock },
  { name: 'Email Monitoring',              icon: Mail },
  { name: 'Duty of Care Outbound Calls',   icon: Heart },
  { name: 'Appointment Booking',           icon: CalendarDays },
  { name: 'Bilingual Support',             icon: Globe },
];

const Pricing = () => (
  <div className="pb-12">
    {/* Hero */}
    <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Pricing tailored to your business</h1>
        <p className="text-xl text-white/70 mb-8">No hidden fees. No long-term lock-ins. We'll build a plan around your call volume, hours, and requirements — then give you a clear quote.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-white text-cygnet-dark px-6 py-3 text-base font-semibold hover:bg-gray-100 transition-colors">
            Get a Custom Quote
          </Link>
          <a href="tel:+442085385400" className="inline-flex items-center justify-center rounded-md border border-white/30 text-white px-6 py-3 text-base font-medium hover:bg-white/10 transition-colors">
            Call 0208 538 5400
          </a>
        </div>
      </div>
    </section>

    {/* Pricing cards */}
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose your level of coverage</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">Tell us which tier fits best and we'll send you a quote within one business day.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              transition={{ delay: i * 0.15 }}
              className={`relative rounded-3xl p-8 shadow-sm h-full flex flex-col ${plan.highlight
                ? 'bg-white border-2 border-cygnet-dark md:-translate-y-4 mt-8 md:mt-0 shadow-lg'
                : 'bg-white border border-gray-200'
              }`}
            >
              {plan.highlight && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cygnet-dark text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                  Most Popular
                </div>
              )}
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
              <p className="text-sm text-gray-400 mb-3">{plan.tagline}</p>
              <p className="text-gray-600 text-sm mb-6 leading-relaxed">{plan.description}</p>
              <ul className="space-y-3.5 mb-8 flex-grow">
                {plan.features.map((feature, j) => (
                  <li key={j} className={`flex items-start gap-3 text-sm ${plan.highlight ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                    <Check className="w-4 h-4 text-cygnet-dark shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className={`mt-auto block text-center rounded-md px-5 py-3 text-sm font-semibold transition-colors ${plan.highlight
                  ? 'bg-cygnet-dark text-white hover:bg-cygnet-DEFAULT'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Get a Quote
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10 text-gray-400 text-sm">
          All plans include a free onboarding call. No setup fees on standard packages. Cancel with 30 days' notice.
        </div>
      </div>
    </section>

    {/* Outbound note */}
    <section className="py-4">
      <div className="mx-auto max-w-4xl px-6">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 md:p-8 text-center">
          <h4 className="text-xl font-bold text-blue-900 mb-3">Important Note on Outbound Calls</h4>
          <p className="text-blue-800 leading-relaxed">
            We don't do sales calls. Our outbound service is exclusively for Duty of Care calls — welfare check-ins, appointment reminders, and follow-ups for existing customers. If you need cold calling or sales outreach, we're not the right fit.
          </p>
        </div>
      </div>
    </section>

    {/* Add-ons */}
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="mx-auto max-w-4xl px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Optional Add-ons</h2>
          <p className="text-gray-500">Bolt on exactly what you need. Each add-on is quoted separately based on your usage.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {addons.map((addon, i) => (
            <div key={i} className="bg-white border border-gray-200 p-6 rounded-xl text-center shadow-sm flex flex-col items-center gap-3">
              <addon.icon className="w-8 h-8 text-cygnet-dark opacity-80" />
              <span className="font-medium text-gray-900">{addon.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Quick FAQ */}
    <section className="py-16 md:py-24 bg-white border-t border-gray-200">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-12">Quick Questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left mb-12">
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Are there setup fees?</h4>
            <p className="text-gray-500 text-sm">No. We don't charge setup fees on standard packages.</p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Do I need to change my number?</h4>
            <p className="text-gray-500 text-sm">No. You just divert your existing number to us.</p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-2">How long are the contracts?</h4>
            <p className="text-gray-500 text-sm">We offer flexible rolling monthly contracts.</p>
          </div>
        </div>
        <Link to="/faq" className="text-cygnet-dark font-medium hover:underline">
          Read all Frequently Asked Questions
        </Link>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 md:py-20 bg-cygnet-dark text-white text-center">
      <div className="mx-auto max-w-2xl px-6">
        <h2 className="text-3xl font-bold mb-4">Not sure which plan fits?</h2>
        <p className="text-lg text-white/80 mb-8">Tell us about your business and call volume — we'll recommend the right setup and send a quote same day.</p>
        <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-white text-cygnet-dark px-8 py-4 text-base font-semibold hover:bg-gray-100 transition-colors">
          Get in Touch <ArrowRight className="ml-2 w-5 h-5" />
        </Link>
      </div>
    </section>
  </div>
);

export default Pricing;
